import {
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { isApiError } from "../api";
import { AuthContext } from "./useAuth";

const actorGenerations = new WeakMap<object, number>();
let nextActorGeneration = 0;
interface PreviewAuthorityState {
  blocked: Set<string>;
  epochs: Map<string, number>;
  listeners: Set<() => void>;
}
const authorityStates = new WeakMap<QueryClient, PreviewAuthorityState>();
function authorityState(client: QueryClient): PreviewAuthorityState {
  let state = authorityStates.get(client);
  if (!state) {
    state = { blocked: new Set(), epochs: new Map(), listeners: new Set() };
    authorityStates.set(client, state);
  }
  return state;
}

function usePreviewAuthority(scope: readonly unknown[]) {
  const client = useQueryClient();
  const authority = authorityState(client);
  const authorityKey = JSON.stringify(scope.slice(0, 3));
  const authorityPrefix = ["program-preview", ...scope.slice(0, 3)];
  const blocked = useSyncExternalStore(
    (listener) => {
      authority.listeners.add(listener);
      return () => {
        authority.listeners.delete(listener);
      };
    },
    () => authority.blocked.has(authorityKey),
    () => false,
  );
  return { client, authority, authorityKey, authorityPrefix, blocked };
}

function revokePreviewAuthority(
  client: QueryClient,
  authority: PreviewAuthorityState,
  authorityKey: string,
  authorityPrefix: readonly unknown[],
): void {
  authority.blocked.add(authorityKey);
  authority.epochs.set(
    authorityKey,
    (authority.epochs.get(authorityKey) ?? 0) + 1,
  );
  void client.cancelQueries({ queryKey: authorityPrefix });
  client.removeQueries({ queryKey: authorityPrefix });
  for (const listener of authority.listeners) listener();
}

/** Exact owner resources share the preview's current actor and denial boundary. */
export function useProgramPreviewQuery<T>({
  scope,
  enabled,
  load,
}: {
  scope: readonly unknown[];
  enabled: boolean;
  load: (signal: AbortSignal, isCurrent: () => boolean) => Promise<T>;
}) {
  const { client, authority, authorityKey, authorityPrefix, blocked } =
    usePreviewAuthority(scope);
  const scopeToken = JSON.stringify(scope);
  const generation = useRef({ scopeToken, value: 0 });
  if (generation.current.scopeToken !== scopeToken) {
    generation.current = { scopeToken, value: generation.current.value + 1 };
  }
  const queryKey = ["program-preview", ...scope];
  const query = useQuery({
    queryKey: blocked ? ["program-preview-blocked", authorityKey] : queryKey,
    queryFn: async ({ signal }) => {
      const captured = generation.current.value;
      const epoch = authority.epochs.get(authorityKey) ?? 0;
      const isCurrent = () =>
        !signal.aborted &&
        generation.current.value === captured &&
        epoch === (authority.epochs.get(authorityKey) ?? 0) &&
        !authority.blocked.has(authorityKey);
      const result = await load(signal, isCurrent);
      if (!isCurrent())
        throw new DOMException("Class request was superseded", "AbortError");
      return result;
    },
    enabled: enabled && !blocked,
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  useEffect(
    () => () => {
      void client.cancelQueries({ queryKey, exact: true });
      client.removeQueries({ queryKey, exact: true });
    },
    [client, scopeToken],
  );
  useEffect(() => {
    if (!enabled) {
      void client.cancelQueries({ queryKey, exact: true });
      client.removeQueries({ queryKey, exact: true });
    }
  }, [client, scopeToken, enabled]);
  useEffect(() => {
    if (isApiError(query.error) && [401, 403].includes(query.error.status)) {
      revokePreviewAuthority(client, authority, authorityKey, authorityPrefix);
    }
  }, [client, query.error, scopeToken]);
  const ready =
    enabled && !blocked && query.isSuccess && query.fetchStatus === "idle";
  return {
    ...query,
    data: ready ? query.data : undefined,
    ready,
    restart: () => {
      if (blocked) {
        authority.blocked.delete(authorityKey);
        authority.epochs.set(
          authorityKey,
          (authority.epochs.get(authorityKey) ?? 0) + 1,
        );
        void client.cancelQueries({ queryKey: authorityPrefix });
        client.removeQueries({ queryKey: authorityPrefix });
        for (const listener of authority.listeners) listener();
      } else {
        void query.refetch();
      }
    },
    isError: blocked || query.isError,
    isLoading:
      enabled && !blocked && (query.isPending || query.fetchStatus !== "idle"),
  };
}

export function useProgramPreviewIdentity(): string {
  const user = useContext(AuthContext)?.user ?? null;
  const signature = JSON.stringify([
    user?.uid ?? null,
    user?.customClaims ?? null,
  ]);
  const identity = useRef({ user, signature, generation: 0 });
  if (
    identity.current.user !== user ||
    identity.current.signature !== signature
  ) {
    identity.current = {
      user,
      signature,
      generation: identity.current.generation + 1,
    };
  }
  if (user && !actorGenerations.has(user))
    actorGenerations.set(user, ++nextActorGeneration);
  return JSON.stringify([
    user ? `authenticated:${user.uid}` : "public",
    user ? actorGenerations.get(user) : 0,
    signature,
    identity.current.generation,
  ]);
}

export interface ProgramPreviewPageOptions<
  T extends { next_cursor: string | null },
> {
  scope: readonly unknown[];
  enabled: boolean;
  initialPage?: T;
  load: (cursor: string | null, signal: AbortSignal) => Promise<T>;
}

/** Live one-page presentation with two retained response/navigation records. */
export function useProgramPreviewPage<
  T extends { next_cursor: string | null },
>({ scope, enabled, initialPage, load }: ProgramPreviewPageOptions<T>) {
  const { client, authority, authorityKey, authorityPrefix, blocked } =
    usePreviewAuthority(scope);
  const scopeToken = JSON.stringify(scope);
  const [navigation, setNavigation] = useState({
    scopeToken,
    cursors: [null] as Array<string | null>,
    selected: 0,
    restart: 0,
  });
  const current =
    navigation.scopeToken === scopeToken
      ? navigation
      : {
          scopeToken,
          cursors: [null] as Array<string | null>,
          selected: 0,
          restart: 0,
        };
  const cursor = current.cursors[current.selected];
  const generation = useRef({ scopeToken, restart: current.restart, value: 0 });
  if (
    generation.current.scopeToken !== scopeToken ||
    generation.current.restart !== current.restart
  ) {
    generation.current = {
      scopeToken,
      restart: current.restart,
      value: generation.current.value + 1,
    };
  }
  const baseKey = ["program-preview", ...scope, current.restart] as const;
  const seed =
    cursor === null && current.restart === 0 ? initialPage : undefined;
  const query = useQuery({
    queryKey: blocked
      ? ["program-preview-blocked", authorityKey]
      : [...baseKey, cursor],
    queryFn: async ({ signal }) => {
      const captured = generation.current.value;
      const epoch = authority.epochs.get(authorityKey) ?? 0;
      const result = await load(cursor, signal);
      if (
        signal.aborted ||
        generation.current.value !== captured ||
        epoch !== (authority.epochs.get(authorityKey) ?? 0)
      ) {
        throw new DOMException(
          "Class preview request was superseded",
          "AbortError",
        );
      }
      return result;
    },
    initialData: seed,
    enabled: enabled && !blocked && seed === undefined,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const previousKey = useRef<readonly unknown[] | null>(null);
  useEffect(() => {
    const previous = previousKey.current;
    if (previous && JSON.stringify(previous) !== JSON.stringify(baseKey)) {
      void client.cancelQueries({ queryKey: previous });
      client.removeQueries({ queryKey: previous });
    }
    previousKey.current = baseKey;
  }, [client, scopeToken, current.restart]);
  useEffect(
    () => () => {
      void client.cancelQueries({ queryKey: baseKey });
      client.removeQueries({ queryKey: baseKey });
    },
    [client, scopeToken, current.restart],
  );
  useEffect(() => {
    for (const entry of client.getQueryCache().findAll({ queryKey: baseKey })) {
      const retainedCursor = entry.queryKey[entry.queryKey.length - 1];
      if (!current.cursors.includes(retainedCursor as string | null)) {
        void client.cancelQueries({ queryKey: entry.queryKey, exact: true });
        client.removeQueries({ queryKey: entry.queryKey, exact: true });
      }
    }
  }, [client, scopeToken, cursor, current.restart]);
  useEffect(() => {
    if (isApiError(query.error) && [401, 403].includes(query.error.status)) {
      revokePreviewAuthority(client, authority, authorityKey, authorityPrefix);
    }
  }, [client, query.error, scopeToken]);
  const ready =
    enabled && !blocked && query.isSuccess && query.fetchStatus === "idle";
  const data = ready ? query.data : undefined;
  function next(): void {
    if (!ready || !data?.next_cursor) return;
    const nextCursor = data.next_cursor;
    setNavigation(
      current.cursors[current.selected + 1] === nextCursor
        ? { ...current, selected: current.selected + 1 }
        : { ...current, cursors: [cursor, nextCursor], selected: 1 },
    );
  }
  function previous(): void {
    if (ready && current.selected > 0)
      setNavigation({ ...current, selected: current.selected - 1 });
  }
  function restart(): void {
    void client.cancelQueries({ queryKey: baseKey });
    client.removeQueries({ queryKey: baseKey });
    authority.blocked.delete(authorityKey);
    for (const listener of authority.listeners) listener();
    setNavigation({
      scopeToken,
      cursors: [null],
      selected: 0,
      restart: current.restart + 1,
    });
  }
  return {
    ...query,
    data,
    ready,
    next,
    previous,
    restart,
    isError: blocked || query.isError,
    canPrevious: current.selected > 0,
    canNext: Boolean(data?.next_cursor),
    isLoading:
      enabled && !blocked && (query.isPending || query.fetchStatus !== "idle"),
  };
}
